import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

const MOBILE_BREAKPOINT = 768;
const TABLET_BREAKPOINT = 1024;

interface DeviceState {
  isMobile: boolean;
  isTablet: boolean;
  hasTouchScreen: boolean;
  isReducedMotion: boolean;
  isMobileDevice: boolean;
  isLandscape: boolean;
  isPortrait: boolean;
}

function getInitialState(): DeviceState {
  if (typeof window === 'undefined') {
    return {
      isMobile: false,
      isTablet: false,
      hasTouchScreen: false,
      isReducedMotion: false,
      isMobileDevice: false,
      isLandscape: false,
      isPortrait: true,
    };
  }
  const w = window.innerWidth;
  return {
    isMobile: w < MOBILE_BREAKPOINT,
    isTablet: w >= MOBILE_BREAKPOINT && w < TABLET_BREAKPOINT,
    hasTouchScreen: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
    isReducedMotion: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    isMobileDevice: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent),
    isLandscape: window.matchMedia('(orientation: landscape)').matches,
    isPortrait: !window.matchMedia('(orientation: landscape)').matches,
  };
}

const DeviceContext = createContext<DeviceState>(getInitialState());

export function DeviceProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<DeviceState>(getInitialState);

  useEffect(() => {
    const mobileQuery = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
    const tabletQuery = window.matchMedia(
      `(min-width: ${MOBILE_BREAKPOINT}px) and (max-width: ${TABLET_BREAKPOINT - 1}px)`
    );
    const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const orientationQuery = window.matchMedia('(orientation: landscape)');

    const handleBreakpoint = () => {
      setState(prev => ({
        ...prev,
        isMobile: mobileQuery.matches,
        isTablet: tabletQuery.matches,
      }));
    };

    const handleReducedMotion = () => {
      setState(prev => ({ ...prev, isReducedMotion: reducedMotionQuery.matches }));
    };

    const handleOrientation = () => {
      setState(prev => ({
        ...prev,
        isLandscape: orientationQuery.matches,
        isPortrait: !orientationQuery.matches,
      }));
    };

    mobileQuery.addEventListener('change', handleBreakpoint);
    tabletQuery.addEventListener('change', handleBreakpoint);
    reducedMotionQuery.addEventListener('change', handleReducedMotion);
    // Use only orientationQuery — do NOT also add window.orientationchange to avoid double-firing
    orientationQuery.addEventListener('change', handleOrientation);

    return () => {
      mobileQuery.removeEventListener('change', handleBreakpoint);
      tabletQuery.removeEventListener('change', handleBreakpoint);
      reducedMotionQuery.removeEventListener('change', handleReducedMotion);
      orientationQuery.removeEventListener('change', handleOrientation);
    };
  }, []);

  return <DeviceContext.Provider value={state}>{children}</DeviceContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useDevice(): DeviceState {
  return useContext(DeviceContext);
}
